import loc, { type LocalizedString, type ResourceId } from 'owa-localize';
import { executePrompt } from './executePrompt';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import { getInboxBriefingCommandText, getCMMIDForInboxBriefing } from './inboxBriefingUtils';
import {
    showTopActionItemsCommandText,
    showTopActionItemsDisplayText,
    findQuickWinsCommandText,
    findQuickWinsDisplayText,
    getOrientedCommandText,
    getOrientedDisplayText,
    latestOnTopicCommandText,
    latestOnTopicDisplayText,
    needsUrgentRepliesCommandText,
    needsUrgentRepliesDisplayText,
    showKeyTakeawaysCommandText,
    showKeyTakeawaysDisplayText,
    showMyTopThreeEmailsCommandText,
    showMyTopThreeEmailsDisplayText,
    showRelatedResourcesCommandText,
    showRelatedResourcesDisplayText,
    suggestNextStepsCommandText,
    suggestNextStepsDisplayText,
    summarizeAndReplyCommandText,
    summarizeAndReplyDisplayText,
    summarizeCommandText,
    summarizeDisplayText,
    triageMyInboxCommandText,
    triageMyInboxDisplayText,
    whatToGetToFirstCommandText,
    whatToGetToFirstDisplayText,
    inboxBriefingDisplayText,
} from './suggestionCatalog.locstring.json';

type BaseSuggestion = {
    /** Unique identifier */
    id: string;
    /** Callback function to execute when the suggestion is clicked */
    onClick: () => void;
};

// Ensures that one of either `displayText` or `displayTextLocalized` is defined
// TODO: Eventually, using only `displayText: LocalizedString` would be ideal
export type Suggestion =
    | (BaseSuggestion & {
          displayText: ResourceId;
          displayTextLocalized?: never;
      })
    | (BaseSuggestion & {
          displayText?: never;
          displayTextLocalized: LocalizedString | string;
      });

type NonEmptyArray<T> = [T, ...T[]];

export type Suggestions = NonEmptyArray<Suggestion> | undefined;

export const findQuickWins: Suggestion = {
    displayText: findQuickWinsDisplayText,
    id: 'findQuickWins',
    onClick: () => executePrompt(loc(findQuickWinsCommandText), 'cmms7bt06i3'),
};

export const getOriented: Suggestion = {
    displayText: getOrientedDisplayText,
    id: 'getOriented',
    onClick: () => executePrompt(loc(getOrientedCommandText), 'cmmmljyih87'),
};

export const suggestNextSteps: Suggestion = {
    displayText: suggestNextStepsDisplayText,
    id: 'suggestNextSteps',
    onClick: () =>
        executePromptWithQueryAnnotation(loc(suggestNextStepsCommandText), 'cmmnga5qm2r'),
};

export const summarize: Suggestion = {
    displayText: summarizeDisplayText,
    id: 'summarize',
    onClick: () => executePromptWithQueryAnnotation(loc(summarizeCommandText), 'cmmemdb07yk'),
};

export const summarizeAndReply: Suggestion = {
    displayText: summarizeAndReplyDisplayText,
    id: 'summarizeAndReply',
    onClick: () =>
        executePromptWithQueryAnnotation(loc(summarizeAndReplyCommandText), 'cmm9uaofbq8'),
};

export const triageMyInbox: Suggestion = {
    displayText: triageMyInboxDisplayText,
    id: 'triageMyInbox',
    onClick: () => executePrompt(loc(triageMyInboxCommandText), 'cmm2syst0oj'),
};

export const showKeyTakeaways: Suggestion = {
    displayText: showKeyTakeawaysDisplayText,
    id: 'showKeyTakeaways',
    onClick: () =>
        executePromptWithQueryAnnotation(loc(showKeyTakeawaysCommandText), 'cmmnaejze13'),
};

export const showRelatedResources: Suggestion = {
    displayText: showRelatedResourcesDisplayText,
    id: 'showRelatedResources',
    onClick: () =>
        executePromptWithQueryAnnotation(loc(showRelatedResourcesCommandText), 'cmmsh5so9e9'),
};

export const needsUrgentReplies: Suggestion = {
    displayText: needsUrgentRepliesDisplayText,
    id: 'needsUrgentReplies',
    onClick: () =>
        executePromptWithQueryAnnotation(loc(needsUrgentRepliesCommandText), 'cmmh7ruakpo'),
};

export const showTopActionItems: Suggestion = {
    displayText: showTopActionItemsDisplayText,
    id: 'showTopActionItems',
    onClick: () => executePrompt(loc(showTopActionItemsCommandText), 'cmmn6jg6pjz'),
};

export const latestOnTopic: Suggestion = {
    displayText: latestOnTopicDisplayText,
    id: 'latestOnTopic',
    onClick: () => executePrompt(loc(latestOnTopicCommandText), 'cmmo9ia57a5'),
};

export const whatToGetToFirst: Suggestion = {
    displayText: whatToGetToFirstDisplayText,
    id: 'whatToGetToFirst',
    onClick: () => executePrompt(loc(whatToGetToFirstCommandText), 'cmmnr4v9nfp'),
};

export const showMyTopThreeEmails: Suggestion = {
    displayText: showMyTopThreeEmailsDisplayText,
    id: 'showMyTopThreeEmails',
    onClick: () => executePrompt(loc(showMyTopThreeEmailsCommandText), 'cmm9byc18nf'),
};

export const inboxBriefing: Suggestion = {
    displayText: inboxBriefingDisplayText,
    id: 'inboxBriefing',
    onClick: () => executePrompt(getInboxBriefingCommandText(), getCMMIDForInboxBriefing()),
};
